// Shared cohort card-order funnel — the single source of truth for the Print Production
// File Tracker and the Ops Health page, so the two can never drift. Mirrors
// agent/run_funnel.py (funnel_sql / lifecycle_sql / _cohort_ctes).
//
// Anchored on the card ORDER (the FIS_BATCH_SENT transition); each order is carried forward by
// the proxy FIS mints for it, so every stage is a strict subset of the prior one (monotone).
// Five stages, each from its own authoritative source:
//   Sunny requested  = FIS_BATCH_SENT send                         (fis.consumer_account_history)
//   Proxy created    = FIS assigns the card proxy                  (fis.consumer_account_history)
//   FIS embossed     = 'Embossing Complete'                        (fis.non_monetary_transaction)
//   AbCorp produced  = AB Corp processing_date                     (ab_corp.workorder_shipping_events)
//   Shipped to member= AB Corp ship_date                           (ab_corp.workorder_shipping_events)
// NOTE: AB Corp only sends a record once a card ships, so "produced" and "shipped" share the same
// rows — the produced COUNT tracks shipped; its value is the DATE (processing_date, ~1-2 days
// before ship). PII firewall: aggregate COUNT / MIN only; proxy is a non-PII card proxy.

// Date guard: only YYYY-MM-DD passes into SQL (from <input type=date>; sanitize anyway).
export const d8 = (s) => (/^\d{4}-\d{2}-\d{2}$/.test(s) ? s : '');

// Business-facing labels for the internal lifecycle keys (data/SQL keys stay stable).
export const STAGE_LABEL = {
  Requested: 'Sunny requested',    // FIS_BATCH_SENT send
  Created:   'Proxy created',      // FIS assigns the card proxy
  Embossed:  'FIS embossed',       // 'Embossing Complete'
  Produced:  'AbCorp produced',    // AB Corp processing_date
  Shipped:   'Shipped to member',  // AB Corp ship_date
};

// Stage chip colors keyed by the internal lifecycle key.
export const STAGE_C = {
  Requested: { bg: '#F1EFE8', fg: '#6b6a64' },
  Created:   { bg: '#EDF3FB', fg: '#3B6EA5' },
  Embossed:  { bg: '#E6F1FB', fg: '#185FA5' },
  Produced:  { bg: '#E7F3EF', fg: '#0F7A63' },
  Shipped:   { bg: '#E1F5EE', fg: '#0F6E56' },
};

// Friendly display names for warehouse tenant_name values. DISPLAY ONLY — the raw
// tenant_name stays the query key (drill/lifecycle filter on WHERE tenant_name = ...).
export const CLIENT_LABEL = {
  'KP Small CAP 2025': 'Kaiser Permanente',
  'HAP-Prod': 'HAP',
};
export const clientLabel = (name) => CLIENT_LABEL[name] || name || '—';

// Shared CTEs: the Requested cohort (transition INTO FIS_BATCH_SENT, windowed on send date) +
// each order's newly-minted proxy. created_at = when that proxy first appears (Proxy created).
// Three order paths, by the status the account transitions FROM:
//   ELIGIBLE_FOR_FIS_BATCH_PROCESS -> member self-order
//   NOT_ELIGIBLE                   -> admin ordered on their behalf
//   NOT_APPLICABLE                 -> bulk/auto issuance (no eligibility gate; e.g. Navitus)
export const cohortCtes = (start, end) => {
  const s = d8(start), e = d8(end);
  return "h AS (SELECT consumer_account_code, consumer_account_history_id AS id, tenant_code, " +
    "create_ts, card_issue_status, proxy_number, proxy_update_ts, " +
    "LAG(card_issue_status) OVER (PARTITION BY consumer_account_code ORDER BY consumer_account_history_id) AS prev_status " +
    "FROM fis.consumer_account_history WHERE delete_nbr=0), " +
    "req AS (SELECT consumer_account_code, id AS req_id, tenant_code, create_ts AS send_ts, " +
    "CASE WHEN prev_status='NOT_ELIGIBLE' THEN 'admin' WHEN prev_status='NOT_APPLICABLE' THEN 'bulk' ELSE 'member' END AS ordered_by, " +
    "LEAD(id) OVER (PARTITION BY consumer_account_code ORDER BY id) AS next_id " +
    "FROM h WHERE card_issue_status='FIS_BATCH_SENT' " +
    "AND prev_status IN ('ELIGIBLE_FOR_FIS_BATCH_PROCESS','NOT_ELIGIBLE','NOT_APPLICABLE')), " +
    "reqw AS (SELECT * FROM req WHERE send_ts::date BETWEEN '" + s + "' AND '" + e + "'), " +
    "cand AS (SELECT r.req_id, r.tenant_code, r.ordered_by, r.send_ts, " +
    "a.proxy_number AS new_proxy, MIN(a.create_ts) OVER (PARTITION BY r.req_id, a.proxy_number) AS created_at, a.client_unique_id AS fis_order_id, " +
    "ROW_NUMBER() OVER (PARTITION BY r.req_id ORDER BY a.proxy_update_ts DESC, a.consumer_account_history_id DESC) AS rn " +
    "FROM reqw r LEFT JOIN fis.consumer_account_history a " +
    "ON a.consumer_account_code=r.consumer_account_code AND a.delete_nbr=0 " +
    "AND a.consumer_account_history_id >= r.req_id " +
    "AND (r.next_id IS NULL OR a.consumer_account_history_id < r.next_id) " +
    "AND a.proxy_number IS NOT NULL AND a.proxy_update_ts >= r.send_ts AND a.proxy_update_ts < '2100-01-01'), " +
    "orders AS (SELECT req_id, tenant_code, ordered_by, send_ts, new_proxy, created_at, fis_order_id " +
    "FROM cand WHERE rn=1)";
};

// Per-CLIENT funnel: requested (+ member/admin split), created, embossed, produced, shipped.
export const funnelSql = (start, end) =>
  "WITH " + cohortCtes(start, end) + ", " +
  "cp AS (SELECT DISTINCT new_proxy AS p FROM orders WHERE new_proxy IS NOT NULL), " +
  "emb AS (SELECT DISTINCT n.card_number_proxy AS p FROM fis.non_monetary_transaction n " +
  "JOIN cp ON cp.p=n.card_number_proxy WHERE n.source_code_desc='Embossing Complete'), " +
  "prod AS (SELECT DISTINCT sh.proxy_number AS p FROM ab_corp.workorder_shipping_events sh " +
  "JOIN cp ON cp.p=sh.proxy_number WHERE sh.processing_date IS NOT NULL), " +
  "shp AS (SELECT DISTINCT sh.proxy_number AS p FROM ab_corp.workorder_shipping_events sh " +
  "JOIN cp ON cp.p=sh.proxy_number WHERE sh.ship_date IS NOT NULL), " +
  "f AS (SELECT o.tenant_code, o.ordered_by, " +
  "CASE WHEN o.new_proxy IS NOT NULL THEN 1 ELSE 0 END AS created, " +
  "CASE WHEN emb.p IS NOT NULL THEN 1 ELSE 0 END AS embossed, " +
  "CASE WHEN prod.p IS NOT NULL THEN 1 ELSE 0 END AS produced, " +
  "CASE WHEN shp.p IS NOT NULL THEN 1 ELSE 0 END AS shipped " +
  "FROM orders o LEFT JOIN emb ON emb.p=o.new_proxy LEFT JOIN prod ON prod.p=o.new_proxy LEFT JOIN shp ON shp.p=o.new_proxy) " +
  "SELECT t.tenant_name, COUNT(*) AS requested, " +
  "SUM(CASE WHEN f.ordered_by='member' THEN 1 ELSE 0 END) AS requested_member, " +
  "SUM(CASE WHEN f.ordered_by='admin' THEN 1 ELSE 0 END) AS requested_admin, " +
  "SUM(CASE WHEN f.ordered_by='bulk' THEN 1 ELSE 0 END) AS requested_bulk, " +
  "SUM(f.created) AS created, SUM(f.embossed) AS embossed, SUM(f.produced) AS produced, SUM(f.shipped) AS shipped " +
  "FROM f JOIN tenant.tenant t ON t.tenant_code=f.tenant_code AND t.delete_nbr=0 " +
  // No tight LIMIT: both pages SUM across these rows for totals/exceptions, so dropping
  // clients silently understates the operational picture. The client cardinality is the
  // number of tenants with orders in the window (tens), so 500 is effectively unbounded
  // for this domain while still guarding the UI against a pathological result.
  "GROUP BY 1 ORDER BY 2 DESC LIMIT 500";

// 5-stage clamps so displayed dates never render backwards (created<=embossed<=produced<=shipped).
// created_at/embossed_at are timestamps; produced/shipped are dates (cast for the mixed LEAST).
// Each clamp is NULL-guarded on its OWN stage's source date: Redshift LEAST ignores NULL args, so
// an un-clamped LEAST would invent, say, an embossed_at from the ship date for a card that shipped
// but has no 'Embossing Complete' event — making the per-card drill count it as embossed while the
// per-client funnelSql (which joins the real emb CTE) does not. The CASE keeps a missing stage NULL.
const CREATED_CLAMP = "CASE WHEN o.created_at IS NULL THEN NULL ELSE LEAST(o.created_at, COALESCE(emb.embossed_at, o.created_at), COALESCE(sh.produced_at::timestamp, o.created_at), COALESCE(sh.shipped_at::timestamp, o.created_at)) END";
const EMBOSSED_CLAMP = "CASE WHEN emb.embossed_at IS NULL THEN NULL ELSE LEAST(emb.embossed_at, COALESCE(sh.produced_at::timestamp, emb.embossed_at), COALESCE(sh.shipped_at::timestamp, emb.embossed_at)) END";
const PRODUCED_CLAMP = "CASE WHEN sh.produced_at IS NULL THEN NULL ELSE LEAST(sh.produced_at, COALESCE(sh.shipped_at, sh.produced_at)) END";
const STAGE_CASE = "CASE WHEN sh.shipped_at IS NOT NULL THEN 'Shipped' WHEN sh.produced_at IS NOT NULL THEN 'Produced' " +
  "WHEN emb.embossed_at IS NOT NULL THEN 'Embossed' WHEN o.new_proxy IS NOT NULL THEN 'Created' ELSE 'Requested' END";

// emb/sh sub-CTEs shared by the per-order + per-card queries.
const embSh = "emb AS (SELECT n.card_number_proxy AS p, MIN(n.timestamp_) AS embossed_at " +
  "FROM fis.non_monetary_transaction n JOIN cp ON cp.p=n.card_number_proxy " +
  "WHERE n.source_code_desc='Embossing Complete' GROUP BY 1), " +
  "sh AS (SELECT sh.proxy_number AS p, MIN(sh.processing_date) AS produced_at, MIN(sh.ship_date) AS shipped_at, MIN(sh.input_file) AS shipped_file " +
  "FROM ab_corp.workorder_shipping_events sh JOIN cp ON cp.p=sh.proxy_number GROUP BY 1)";

// Per-ORDER lifecycle for one client — one row per card order, each stamped with the five stage
// dates it has reached + its current stage. KEYSET-paginated on req_id (the unique, immutable
// per-order PK), newest first: each page asks for req_id < the last row seen. OFFSET paging would
// drift because the default window includes today — a newer order arriving between page fetches
// shifts every offset by one, so the next page repeats or skips a row. A req_id cursor is immune:
// already-seen rows keep their place no matter what arrives at the top. req_id tracks creation
// order, so DESC is effectively newest-first; the UI groups rows into Card30 files by send day.
export const lifecycleSql = (tenant, start, end, limit, beforeReqId = null) => {
  const t = String(tenant).replace(/'/g, "''");
  const before = parseInt(beforeReqId, 10);
  const cursor = Number.isNaN(before) ? "" : " AND o.req_id < " + before;
  return "WITH " + cohortCtes(start, end) + ", " +
    "cp AS (SELECT DISTINCT new_proxy AS p FROM orders WHERE new_proxy IS NOT NULL), " + embSh + " " +
    // o.req_id (the unique per-order PK) is selected so the UI has a stable, collision-free row
    // key even for un-minted orders, whose fis_order_id and proxy are both NULL — and so each page
    // can hand back the last req_id as the next page's cursor.
    "SELECT o.req_id, 'Card30_SunnyB_' || to_char(o.send_ts,'YYYYMMDD') AS file_tag, " +
    "o.fis_order_id, o.new_proxy AS proxy, o.ordered_by, " +
    "o.send_ts AS requested_at, " + CREATED_CLAMP + " AS created_at, " + EMBOSSED_CLAMP + " AS embossed_at, " +
    PRODUCED_CLAMP + " AS produced_at, sh.shipped_at, sh.shipped_file, " + STAGE_CASE + " AS current_stage " +
    "FROM orders o JOIN tenant.tenant t ON t.tenant_code=o.tenant_code AND t.delete_nbr=0 " +
    "LEFT JOIN emb ON emb.p=o.new_proxy LEFT JOIN sh ON sh.p=o.new_proxy " +
    "WHERE t.tenant_name = '" + t + "'" + cursor + " " +
    "ORDER BY o.req_id DESC LIMIT " + (parseInt(limit, 10) || 500);
};

// Per-MEMBER card-status lookup by client member id (huser.consumer.member_id -> consumer_code
// -> cohort). ONE ROW PER CARD (so reissues each show) with the five stage dates + current stage.
// PII firewall: reads huser.consumer only to map member_id -> consumer_code (never name/DOB);
// output is card status only — proxy + stage dates, NO PHI. Member id escaped; exact-match only.
export const memberLookupSql = (memberId) => {
  const mid = String(memberId).replace(/'/g, "''");
  return "WITH m AS (SELECT DISTINCT consumer_code, tenant_code FROM huser.consumer " +
    "WHERE member_id = '" + mid + "' AND delete_nbr=0), " +
    "h AS (SELECT consumer_account_code, consumer_account_history_id AS id, tenant_code, create_ts, " +
    "card_issue_status, proxy_number, proxy_update_ts, client_unique_id, " +
    "LAG(card_issue_status) OVER (PARTITION BY consumer_account_code ORDER BY consumer_account_history_id) AS prev_status " +
    "FROM fis.consumer_account_history WHERE delete_nbr=0 AND consumer_code IN (SELECT consumer_code FROM m)), " +
    "req AS (SELECT consumer_account_code, id AS req_id, tenant_code, create_ts AS send_ts, " +
    "CASE WHEN prev_status='NOT_ELIGIBLE' THEN 'admin' WHEN prev_status='NOT_APPLICABLE' THEN 'bulk' ELSE 'member' END AS ordered_by, " +
    "LEAD(id) OVER (PARTITION BY consumer_account_code ORDER BY id) AS next_id " +
    "FROM h WHERE card_issue_status='FIS_BATCH_SENT' AND prev_status IN ('ELIGIBLE_FOR_FIS_BATCH_PROCESS','NOT_ELIGIBLE','NOT_APPLICABLE')), " +
    "cards AS (SELECT r.req_id, r.tenant_code, r.ordered_by, r.send_ts, a.proxy_number AS proxy, " +
    "MIN(a.create_ts) AS card_created, MIN(a.client_unique_id) AS fis_order_id " +
    "FROM req r JOIN fis.consumer_account_history a ON a.consumer_account_code=r.consumer_account_code AND a.delete_nbr=0 " +
    "AND a.consumer_account_history_id >= r.req_id AND (r.next_id IS NULL OR a.consumer_account_history_id < r.next_id) " +
    "AND a.proxy_number IS NOT NULL AND a.proxy_update_ts >= r.send_ts AND a.proxy_update_ts < '2100-01-01' " +
    "GROUP BY r.req_id, r.tenant_code, r.ordered_by, r.send_ts, a.proxy_number), " +
    "cardseq AS (SELECT c.*, ROW_NUMBER() OVER (PARTITION BY req_id ORDER BY card_created, proxy) AS card_seq FROM cards c), " +
    "cp AS (SELECT DISTINCT proxy AS p FROM cards), " +
    "emb AS (SELECT n.card_number_proxy AS p, MIN(n.timestamp_) AS embossed_at FROM fis.non_monetary_transaction n " +
    "JOIN cp ON cp.p=n.card_number_proxy WHERE n.source_code_desc='Embossing Complete' GROUP BY 1), " +
    "sh AS (SELECT sh.proxy_number AS p, MIN(sh.processing_date) AS produced_at, MIN(sh.ship_date) AS shipped_at " +
    "FROM ab_corp.workorder_shipping_events sh JOIN cp ON cp.p=sh.proxy_number GROUP BY 1) " +
    "SELECT t.tenant_name, 'Card30_SunnyB_' || to_char(c.send_ts,'YYYYMMDD') AS file_tag, " +
    "c.fis_order_id, c.proxy, c.ordered_by, c.card_seq, " +
    "CASE WHEN c.card_seq > 1 THEN NULL ELSE c.send_ts END AS requested_at, " +
    "LEAST(c.card_created, COALESCE(emb.embossed_at, c.card_created), COALESCE(sh.produced_at::timestamp, c.card_created), COALESCE(sh.shipped_at::timestamp, c.card_created)) AS created_at, " +
    // NULL-guarded like the lifecycleSql clamps: a card with ship data but no embossing event must
    // keep a NULL embossed_at (Redshift LEAST would otherwise invent one from the later stages).
    "CASE WHEN emb.embossed_at IS NULL THEN NULL ELSE LEAST(emb.embossed_at, COALESCE(sh.produced_at::timestamp, emb.embossed_at), COALESCE(sh.shipped_at::timestamp, emb.embossed_at)) END AS embossed_at, " +
    "CASE WHEN sh.produced_at IS NULL THEN NULL ELSE LEAST(sh.produced_at, COALESCE(sh.shipped_at, sh.produced_at)) END AS produced_at, sh.shipped_at, " +
    "CASE WHEN sh.shipped_at IS NOT NULL THEN 'Shipped' WHEN sh.produced_at IS NOT NULL THEN 'Produced' " +
    "WHEN emb.embossed_at IS NOT NULL THEN 'Embossed' ELSE 'Created' END AS current_stage " +
    "FROM cardseq c JOIN tenant.tenant t ON t.tenant_code=c.tenant_code AND t.delete_nbr=0 " +
    "LEFT JOIN emb ON emb.p=c.proxy LEFT JOIN sh ON sh.p=c.proxy " +
    "ORDER BY c.send_ts DESC, c.card_seq LIMIT 100";
};
